import { EventKind } from '../sim/types';
import type { SimEvent } from '../sim/types';
import { warheadDef } from '../content/combat';
import { worldToScreenX, worldToScreenY } from './projection';
import type { Camera, Viewport } from './projection';
import { TILE } from '../sim/constants';

/*
 * אפקטים ויזואליים.
 *
 * **הכול כאן מחוץ לסימולציה, וזה לא פרט טכני.** ניצוץ, עשן ומכתש לא משנים אף
 * תוצאה, ולכן אין להם מקום במצב המשחק: הם היו מנפחים את ה-hash ואת השמירה, והיו
 * מכריחים כל מחשב במולטיפלייר לחשב את אותם חלקיקים. הם נבנים מ-`state.events` —
 * פלט חד-כיווני של הסימולציה — ומשתמשים ב-Math.random בחופשיות, כי שום דבר כאן
 * לא נכנס חזרה למשחק.
 *
 * זו הסיבה שהמשחק יכול להיראות עשיר בלי לוותר על שורה אחת מהדטרמיניזם.
 */

interface Particle {
    x: number;
    y: number;
    vx: number;
    vy: number;
    life: number;
    maxLife: number;
    size: number;
    kind: 'spark' | 'smoke' | 'fire' | 'debris';
}

interface Decal {
    x: number;
    y: number;
    radius: number;
    life: number;
    maxLife: number;
}

interface Flash {
    x: number;
    y: number;
    angle: number;
    life: number;
    size: number;
}

/** תקרות — אחרת קרב ארוך צובר חלקיקים עד שהפריים נופל. */
const MAX_PARTICLES = 900;
const MAX_DECALS = 120;

export class Effects {
    private readonly particles: Particle[] = [];
    private readonly decals: Decal[] = [];
    private readonly flashes: Flash[] = [];

    clear(): void {
        this.particles.length = 0;
        this.decals.length = 0;
        this.flashes.length = 0;
    }

    /** קורא את אירועי הטיק ומוליד מהם חלקיקים. */
    consume(events: readonly SimEvent[]): void {
        for (const event of events) {
            if (event.kind === EventKind.Shot) {
                const angle = Math.atan2(event.b - event.y, event.a - event.x);
                this.flashes.push({ x: event.x, y: event.y, angle, life: 1, size: 26 });
            } else if (event.kind === EventKind.Impact) {
                const blast = warheadDef(event.a).effect === 'blast';
                if (event.b === 1) this.impact(event.x, event.y, blast);
            } else if (event.kind === EventKind.Death) {
                this.explosion(event.x, event.y);
            }
        }
    }

    private impact(x: number, y: number, blast: boolean): void {
        const sparks = blast ? 14 : 5;
        for (let i = 0; i < sparks; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = (blast ? 90 : 45) * (0.4 + Math.random() * 0.6);
            this.push({
                x,
                y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                life: 1,
                maxLife: blast ? 0.45 : 0.25,
                size: blast ? 9 : 5,
                kind: 'spark',
            });
        }

        if (!blast) return;

        for (let i = 0; i < 5; i++) {
            const angle = Math.random() * Math.PI * 2;
            this.push({
                x,
                y,
                vx: Math.cos(angle) * 22,
                vy: Math.sin(angle) * 22 - 12,
                life: 1,
                maxLife: 1.1,
                size: 34,
                kind: 'smoke',
            });
        }
        this.push({ x, y, vx: 0, vy: 0, life: 1, maxLife: 0.28, size: 62, kind: 'fire' });
        this.addDecal(x, y, 46, 14);
    }

    private explosion(x: number, y: number): void {
        this.push({ x, y, vx: 0, vy: 0, life: 1, maxLife: 0.4, size: 130, kind: 'fire' });

        for (let i = 0; i < 22; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = 150 * (0.3 + Math.random() * 0.7);
            this.push({
                x,
                y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                life: 1,
                maxLife: 0.6 + Math.random() * 0.3,
                size: 11,
                kind: 'spark',
            });
        }

        for (let i = 0; i < 10; i++) {
            const angle = Math.random() * Math.PI * 2;
            this.push({
                x,
                y,
                vx: Math.cos(angle) * 70,
                vy: Math.sin(angle) * 70,
                life: 1,
                maxLife: 0.8,
                size: 14,
                kind: 'debris',
            });
        }

        for (let i = 0; i < 9; i++) {
            const angle = Math.random() * Math.PI * 2;
            this.push({
                x,
                y,
                vx: Math.cos(angle) * 30,
                vy: Math.sin(angle) * 30 - 18,
                life: 1,
                maxLife: 1.8 + Math.random(),
                size: 60,
                kind: 'smoke',
            });
        }

        this.addDecal(x, y, 105, 40);
    }

    private push(particle: Particle): void {
        // מחליפים את הוותיק ביותר במקום לגדול בלי גבול.
        if (this.particles.length >= MAX_PARTICLES) this.particles.shift();
        this.particles.push(particle);
    }

    private addDecal(x: number, y: number, radius: number, seconds: number): void {
        if (this.decals.length >= MAX_DECALS) this.decals.shift();
        this.decals.push({ x, y, radius, life: 1, maxLife: seconds });
    }

    update(deltaMs: number): void {
        const dt = Math.min(deltaMs, 100) / 1000;

        for (let i = this.particles.length - 1; i >= 0; i--) {
            const p = this.particles[i];
            if (!p) continue;
            p.life -= dt / p.maxLife;
            if (p.life <= 0) {
                this.particles.splice(i, 1);
                continue;
            }
            p.x += p.vx * dt;
            p.y += p.vy * dt;
            // גרר: החלקיקים מאטים במקום לעוף לנצח.
            p.vx *= 1 - Math.min(1, dt * (p.kind === 'smoke' ? 1.2 : 3.4));
            p.vy *= 1 - Math.min(1, dt * (p.kind === 'smoke' ? 1.2 : 3.4));
            if (p.kind === 'smoke') p.vy -= 6 * dt;
        }

        for (let i = this.decals.length - 1; i >= 0; i--) {
            const d = this.decals[i];
            if (!d) continue;
            d.life -= dt / d.maxLife;
            if (d.life <= 0) this.decals.splice(i, 1);
        }

        for (let i = this.flashes.length - 1; i >= 0; i--) {
            const f = this.flashes[i];
            if (!f) continue;
            f.life -= dt / 0.07;
            if (f.life <= 0) this.flashes.splice(i, 1);
        }
    }

    /** מכתשים וכתמי שריפה — מצוירים מתחת ליחידות. */
    drawGround(ctx: CanvasRenderingContext2D, cam: Camera, vp: Viewport): void {
        const scale = cam.zoom / TILE;
        for (const decal of this.decals) {
            const x = worldToScreenX(decal.x, cam, vp);
            const y = worldToScreenY(decal.y, cam, vp);
            const r = decal.radius * scale;
            if (x + r < 0 || y + r < 0 || x - r > vp.width || y - r > vp.height) continue;
            ctx.globalAlpha = Math.min(0.55, decal.life * 0.55);
            ctx.fillStyle = '#120e0a';
            ctx.beginPath();
            ctx.arc(x, y, r, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.globalAlpha = 1;
    }

    /** ניצוצות, אש ועשן — מצוירים מעל היחידות. */
    drawAir(ctx: CanvasRenderingContext2D, cam: Camera, vp: Viewport): void {
        const scale = cam.zoom / TILE;

        for (const flash of this.flashes) {
            const x = worldToScreenX(flash.x, cam, vp);
            const y = worldToScreenY(flash.y, cam, vp);
            const size = flash.size * scale * flash.life;
            if (size < 0.5) continue;
            ctx.save();
            ctx.translate(x, y);
            ctx.rotate(flash.angle);
            ctx.globalAlpha = flash.life;
            ctx.fillStyle = '#ffe9a8';
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.lineTo(size, -size * 0.32);
            ctx.lineTo(size * 1.25, 0);
            ctx.lineTo(size, size * 0.32);
            ctx.closePath();
            ctx.fill();
            ctx.restore();
        }

        for (const p of this.particles) {
            const x = worldToScreenX(p.x, cam, vp);
            const y = worldToScreenY(p.y, cam, vp);
            const size = p.size * scale;
            if (size < 0.4) continue;
            if (x + size < 0 || y + size < 0 || x - size > vp.width || y - size > vp.height) continue;

            switch (p.kind) {
                case 'spark':
                    ctx.globalAlpha = p.life;
                    ctx.fillStyle = p.life > 0.6 ? '#fff3c4' : p.life > 0.3 ? '#ffa93b' : '#e2562a';
                    ctx.fillRect(x - size / 2, y - size / 2, size, size);
                    break;
                case 'fire':
                    ctx.globalAlpha = p.life * 0.9;
                    ctx.fillStyle = p.life > 0.5 ? '#fff0b0' : '#ff8a2b';
                    ctx.beginPath();
                    ctx.arc(x, y, (size / 2) * (1.4 - p.life * 0.5), 0, Math.PI * 2);
                    ctx.fill();
                    break;
                case 'smoke':
                    ctx.globalAlpha = p.life * 0.32;
                    ctx.fillStyle = '#6b6f75';
                    ctx.beginPath();
                    ctx.arc(x, y, (size / 2) * (1.7 - p.life), 0, Math.PI * 2);
                    ctx.fill();
                    break;
                case 'debris':
                    ctx.globalAlpha = p.life;
                    ctx.fillStyle = '#2a2d33';
                    ctx.fillRect(x - size / 3, y - size / 3, size * 0.66, size * 0.66);
                    break;
            }
        }

        ctx.globalAlpha = 1;
    }

    get particleCount(): number {
        return this.particles.length;
    }

    get decalCount(): number {
        return this.decals.length;
    }

    get flashCount(): number {
        return this.flashes.length;
    }
}
